import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { CardsCountBadge_userCardCollectionSlot } from './__generated__/index.graphql';

const Wrapper = styled.div`
  position: relative;
`;
const StyledButtonBase = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -2px;
  right: -2px;
  width: min(32px, 40%);
  height: min(32px, 40%);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 29% 100%, 0 71%);
  background: linear-gradient(124deg, #515151 33.02%, #141414 109.93%);
  filter: drop-shadow(0 0 4px #000);
  padding: 0 0 2px 1px;
  transition: scale 0.4s ease-in-out;
  &:hover {
    scale: 1.1;
  }
  &.small {
    top: -1px;
    right: -1px;
    width: min(16px, 40%);
    height: min(16px, 40%);
    filter: drop-shadow(0 0 1px #000);
    &:hover {
      scale: 1.2;
    }
  }
`;

type Props = {
  size: 'small' | 'medium';
  userCardCollectionSlot: Nullable<CardsCountBadge_userCardCollectionSlot>;
  rarity: Nullable<Rarity>;
};

export const CardsCountBadge = ({
  size,
  userCardCollectionSlot,
  rarity,
  children,
}: PropsWithChildren<Props>) => {
  const { generateSportContextPath } = useSportContext();
  const { cardCollectionCardsCount, highlightedCardCollectionCard } =
    userCardCollectionSlot || {};
  const { anyCard } = highlightedCardCollectionCard || {};
  const Text = size === 'small' ? BrandXXS : BrandS;
  return (
    <Wrapper>
      {children}
      <StyledButtonBase
        to={
          // TODO: handle it for other users
          // https://linear.app/sorare/issue/MAR-1965/handle-my-club-gallery
          `${generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
            params: {
              type: rarity || Rarity.common,
            },
          })}?${buildFilterQuery({
            [SEARCH_PARAMS.QUERY]: anyCard?.anyPlayer?.displayName ?? '',
            // TODO: add series filter
          })}`
        }
        className={size}
      >
        <Text>{cardCollectionCardsCount}</Text>
      </StyledButtonBase>
    </Wrapper>
  );
};

CardsCountBadge.fragments = {
  userCardCollectionSlot: gql`
    fragment CardsCountBadge_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      cardCollectionCardsCount
      highlightedCardCollectionCard {
        id
        anyCard {
          slug
          anyPlayer {
            slug
            displayName
          }
        }
      }
    }
  ` as TypedDocumentNode<CardsCountBadge_userCardCollectionSlot>,
};
